import {Modal} from "../../components/Modal/Modal";
import React from "react";
import styled, {keyframes} from "styled-components";
import {Button} from "../../components/Button";
import {useAppContext} from "../../provider/AppProvider";
import {useYandexSdk} from "../../provider/yandex/yandexSdk";
import {getSizeForLayout} from "../../utils/getSizeForLayout";
import gameOverBackground from './../../assets/modal_game_over.jpg';
import {useViewPortState} from "../../provider/ResizeProvider";

interface Props {
    isGameOver: boolean;
    score: number;
    restart: () => void;
    removeRows: (p: number) => void;
    totalScore: number;
    videoWasWatched: boolean;
}

const Root = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
`;

const Row = styled.div`
    display: flex;
    gap: 16px;
`;

const scaleAnimation = keyframes`
    0%, 100% {
        transform: scale(1);
    }
    50% {
        transform: scale(0.9);
    }
`;

const TitleWrapper = styled.div`
    max-width: 190px;
    margin-top: 24px;
    animation: ${scaleAnimation} 2s infinite ease-in-out;

    @media (min-width: 768px) {
        max-width: 250px;
    }

    @media (min-width: 1024px) {
        max-width: 350px;
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-grow: 1;
    justify-content: space-evenly;
`;

const BlockText = styled.div`
    font-weight: 900;
    color: #fff;
    text-align: center;
    font-size: 22px;

    @media (min-width: 768px) {
        font-size: 28px;
    }

    @media (min-width: 1024px) {
        font-size: 52px;
    }
`;

const Title = styled.div``;

const Text = styled.div`
    font-size: 20px;
    line-height: 18px;

    @media (min-width: 768px) {
        font-size: 26px;
        line-height: 24px;
    }

    @media (min-width: 1024px) {
        font-size: 50px;
        line-height: 48px;
    }
`;

const TextBold = styled.div`
    font-weight: 900;
    color: white;
    font-size: 22px;
    filter: drop-shadow(4px 3px 6px black);

    @media (min-width: 768px) {
        font-size: 32px;
    }

    @media (min-width: 1024px) {
        font-size: 44px;
    }
`;

const Image = styled.img`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    transform: rotate(90deg);
    border-radius: 20px;
    object-fit: fill;

    -webkit-user-select: none; /* Safari */
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-tap-highlight-color: transparent;

    pointer-events: none; /* Prevent any interactions */
    user-drag: none; /* Disable image dragging */
    -webkit-user-drag: none; /* Disable image dragging for Safari */
`;

export const GameOverModal = ({isGameOver, score, restart, removeRows, totalScore, videoWasWatched}: Props) => {
    const {isTableState, isDesktopState} = useViewPortState();
    const {yandexSdk} = useYandexSdk();
    const {translate, setScreen, onPlayMusic, onStopMusic, sound, mode} = useAppContext();

    return (
        <Modal isOpen={isGameOver} onClose={() => {
        }}>
            <Image src={gameOverBackground}/>
            <Root>
                <TitleWrapper>
                    {videoWasWatched ? (
                        <TextBold>{translate.end}</TextBold>
                    ) : (
                        <Button size={getSizeForLayout({
                            m: 40,
                            t: 60,
                            d: 80,
                            isTable: isTableState,
                            isDesktop: isDesktopState
                        })} color='#1c188c' text={translate.video}
                                iconName='heart'
                                onClick={() => {
                                    yandexSdk.adv.showRewardedVideo({
                                        callbacks: {
                                            onOpen: () => {
                                                onStopMusic();
                                            },
                                            onRewarded: () => {
                                                removeRows(3);
                                            },
                                            onClose: () => {
                                                if (sound) {
                                                    onPlayMusic();
                                                }
                                            },
                                            onError: (e: any) => {
                                                console.log('Error while open video ad:', e);
                                            }
                                        }
                                    })
                                }}/>
                    )}
                </TitleWrapper>
                <Content>
                    <Row>
                        <BlockText>
                            <Title>{translate.score}</Title>
                            <Text>{score}</Text>
                        </BlockText>
                        <BlockText>
                            <Title>{translate.total}</Title>
                            <Text>{totalScore}</Text>
                        </BlockText>
                    </Row>
                    <Row>
                        <Button size={getSizeForLayout({
                            m: 40,
                            t: 60,
                            d: 80,
                            isTable: isTableState,
                            isDesktop: isDesktopState
                        })} color='#1c188c' text='' iconName='home' onClick={() => {
                            yandexSdk.adv.showFullscreenAdv({
                                callbacks: {
                                    onOpen: function () {
                                        onStopMusic();
                                    },
                                    onClose: function () {
                                        if (sound) {
                                            onPlayMusic();
                                        }
                                        setScreen('start', mode);
                                        // Действие после закрытия рекламы.
                                    },
                                    onError: function () {
                                        setScreen('start', mode);
                                        // Действие в случае ошибки.
                                    }
                                }
                            })
                        }}/>
                        <Button size={getSizeForLayout({
                            m: 40,
                            t: 60,
                            d: 80,
                            isTable: isTableState,
                            isDesktop: isDesktopState
                        })} color='#1c188c' text='' iconName='retry' onClick={() => {
                            yandexSdk.adv.showFullscreenAdv({
                                callbacks: {
                                    onOpen: function () {
                                        onStopMusic();
                                    },
                                    onClose: function () {
                                        restart();
                                        if (sound) {
                                            onPlayMusic();
                                        }
                                        // Действие после закрытия рекламы.
                                    },
                                    onError: function () {
                                        restart();
                                        // Действие в случае ошибки.
                                    }
                                }
                            })
                        }}/>
                    </Row>
                </Content>
            </Root>
        </Modal>
    );
};